import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { v1URL } from '../../../../../../../shared/constants/urls.constants';
import {
  Risk, RiskConfig, RiskHeatMap, RiskImportPreview, RiskInput, RiskListQuery, RiskUpdate,
} from './risk-register.models';
import { serverErrorMessage } from './risk-register.util';

export interface ListResponse<T> {
  data: T[];
  meta: { totalRecords: number };
}
interface Envelope<T> {
  data: T;
}
type RiskAction = 'approve' | 'reject' | 'close' | 'reopen';

/**
 * Risk register HTTP client for the api's /v1/risk router. Same shape as
 * MgvwRecordsService: JSON through AppHttpService (host-less paths, arrays
 * serialised as status[]=…), binaries as blobs with the server's
 * Content-Disposition filename. `refresh$` is fired by the panel/dialogs
 * after any write so the list and heat map refetch.
 */
@Injectable({ providedIn: 'root' })
export class RiskRegisterService {
  refresh$ = new Subject<void>();

  private appHttpService = inject(AppHttpService);
  private http = inject(HttpClient);

  list(projectId: string, qp: RiskListQuery): Observable<ListResponse<Risk>> {
    return this.appHttpService.getService(v1URL.risk.list({ projectId }), qp);
  }

  heatMap(projectId: string): Observable<RiskHeatMap> {
    return this.appHttpService.getService(v1URL.risk.heatMap({ projectId })).pipe(map((r: Envelope<RiskHeatMap>) => r?.data));
  }

  config(projectId: string): Observable<RiskConfig> {
    return this.appHttpService.getService(v1URL.risk.config({ projectId })).pipe(map((r: Envelope<RiskConfig>) => r?.data));
  }

  saveConfig(projectId: string, cfg: RiskConfig): Observable<RiskConfig> {
    return this.appHttpService.putService(v1URL.risk.config({ projectId }), cfg).pipe(map((r: Envelope<RiskConfig>) => r?.data));
  }

  get(riskId: string): Observable<Risk> {
    return this.appHttpService.getService(v1URL.risk.one({ riskId })).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  updates(riskId: string, start = 0, total = 20): Observable<ListResponse<RiskUpdate>> {
    return this.appHttpService.getService(v1URL.risk.updates({ riskId }), { start, total });
  }

  create(projectId: string, body: RiskInput): Observable<Risk> {
    return this.appHttpService.postService(v1URL.risk.create({ projectId }), body).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  propose(projectId: string, body: RiskInput): Observable<Risk> {
    return this.appHttpService.postService(v1URL.risk.propose({ projectId }), body).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  update(riskId: string, body: Partial<RiskInput>): Observable<Risk> {
    return this.appHttpService.patchService(v1URL.risk.one({ riskId }), body).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  delete(riskId: string): Observable<unknown> {
    return this.appHttpService.deleteService(v1URL.risk.one({ riskId }));
  }

  addUpdate(riskId: string, body: { comment: string; probability?: number | null; impact?: number | null; attachments?: string[] }): Observable<RiskUpdate> {
    return this.appHttpService.postService(v1URL.risk.updates({ riskId }), body).pipe(map((r: Envelope<RiskUpdate>) => r?.data));
  }

  action(riskId: string, action: RiskAction, comment?: string): Observable<Risk> {
    return this.appHttpService.postService(v1URL.risk.action({ riskId, action }), { comment: comment ?? null }).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  convert(riskId: string, body: { type: string; title: string; description?: string; internal?: boolean }): Observable<Risk> {
    return this.appHttpService.postService(v1URL.risk.action({ riskId, action: 'convert-to-issue' }), body).pipe(map((r: Envelope<Risk>) => r?.data));
  }

  importPreview(projectId: string, file: File, sheetName?: string): Observable<RiskImportPreview> {
    return this.upload(projectId, file, sheetName, true);
  }

  import(projectId: string, file: File, sheetName?: string): Observable<RiskImportPreview> {
    return this.upload(projectId, file, sheetName, false);
  }

  private upload(projectId: string, file: File, sheetName: string | undefined, dryRun: boolean): Observable<RiskImportPreview> {
    const form = new FormData();
    form.append('inputFile', file, file.name);
    if (sheetName) form.append('sheet_name', sheetName);
    const url = `${this.appHttpService.API_ENDPOINT}${v1URL.risk.import({ projectId })}?dry_run=${dryRun}`;
    return this.http.post(url, form).pipe(map((r: Envelope<RiskImportPreview>) => r?.data));
  }

  /** Downloads the xlsx; the filename comes from Content-Disposition. */
  export(projectId: string, opts: { status: string[]; internal: 'exclude' | 'include' }, fallbackName: string): Promise<void> {
    return this.appHttpService
      .getService(v1URL.risk.export({ projectId }), opts, { responseType: 'blob' as 'json', observe: 'response' })
      .toPromise()
      .then(async (resp: HttpResponse<Blob> | null) => {
        if (resp?.status === 200 && resp.body instanceof Blob) {
          let name = fallbackName;
          const disposition = resp.headers?.get('Content-Disposition');
          const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (match?.[1]) name = match[1].replace(/['"]/g, '');
          saveAs(resp.body, name);
          return;
        }
        const text = resp?.body instanceof Blob ? await resp.body.text() : '';
        let message = 'Export failed';
        try { message = serverErrorMessage(JSON.parse(text), message); } catch { /* keep default */ }
        throw new Error(message);
      }, async (err: { message?: string; error?: unknown }) => {
        // an error status arrives with the JSON body as a Blob
        let message = err?.message || 'Export failed';
        if (err?.error instanceof Blob) {
          try { message = serverErrorMessage(JSON.parse(await err.error.text()), message); } catch { /* keep default */ }
        }
        throw new Error(message);
      });
  }
}
